import React from 'react';
import {
  LayoutDashboard,
  Users,
  Stethoscope,
  Calendar,
  DollarSign,
  UserCheck,
  CalendarDays,
  User,
  ChevronDown,
  ChevronRight,
  Database,
  Building,
  MapPin,
  Navigation,
  ClipboardList,
  Map,
  PhoneMissed,
  BarChart2,
  FileText,
  KeyRound,
  Smartphone,
  Settings,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ChangePasswordModal from '../common/ChangePasswordModal';

interface SidebarItem {
  icon: React.ElementType;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

interface SidebarGroup {
  title: string;
  icon: React.ElementType;
  items: SidebarItem[];
  isExpanded?: boolean;
}

interface SidebarProps {
  onLogout?: () => void;
  currentPage?: string;
  onNavigate?: (page: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onLogout, currentPage = 'dashboard', onNavigate, isOpen = false, onClose }) => {
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());
  const [showChangePassword, setShowChangePassword] = React.useState(false);
  const { user } = useAuth()
  const navigationGroups: SidebarGroup[] = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      items: [
        {
          icon: LayoutDashboard,
          label: 'Dashboard',
          isActive: currentPage === 'dashboard',
          onClick: () => onNavigate?.('dashboard')
        }
      ]
    },
    {
      title: 'Visit Planning',
      icon: Calendar,
      items: [
        {
          icon: Calendar,
          label: 'Visit Planning',
          isActive: currentPage === 'visit-planning',
          onClick: () => onNavigate?.('visit-planning')
        }
      ]
    },
    {
      title: 'People',
      icon: Users,
      items: [
        {
          icon: Stethoscope,
          label: 'Doctor',
          isActive: currentPage === 'doctors',
          onClick: () => onNavigate?.('doctors')
        },
        {
          icon: Building,
          label: 'Firm',
          isActive: currentPage === 'firms',
          onClick: () => onNavigate?.('firms')
        },
        {
          icon: Users,
          label: 'Employee',
          isActive: currentPage === 'employees',
          onClick: () => onNavigate?.('employees')
        }
      ]
    },
    {
      title: 'Calendar',
      icon: CalendarDays,
      items: [
        {
          icon: CalendarDays,
          label: 'Holiday',
          isActive: currentPage === 'holidays',
          onClick: () => onNavigate?.('holidays')
        },
        {
          icon: UserCheck,
          label: 'Leave',
          isActive: currentPage === 'hr-leave',
          onClick: () => onNavigate?.('hr-leave')
        },
        {
          icon: FileText,
          label: 'Tour Plans',
          isActive: currentPage === 'tour-plans',
          onClick: () => onNavigate?.('tour-plans')
        }
      ]
    },
    {
      title: 'Expenses',
      icon: DollarSign,
      items: [
        {
          icon: DollarSign,
          label: 'Expenses',
          isActive: currentPage === 'expenses',
          onClick: () => onNavigate?.('expenses')
        },
        // {
        //   icon: DollarSign,
        //   label: 'Allowance Settings',
        //   isActive: currentPage === 'allowance-settings',
        //   onClick: () => onNavigate?.('allowance-settings')
        // },
        // {
        //   icon: MapPin,
        //   label: 'SFC',
        //   isActive: currentPage === 'sfc',
        //   onClick: () => onNavigate?.('sfc')
        // }
      ]
    },
    {
      title: 'Attendance',
      icon: UserCheck,
      items: [
        {
          icon: UserCheck,
          label: 'Attendance',
          isActive: currentPage === 'attendance',
          onClick: () => onNavigate?.('attendance'),
        },
      ],
    },
    {
      title: 'Reports',
      icon: BarChart2,
      items: [
        {
          icon: ClipboardList,
          label: 'Call Reports',
          isActive: currentPage === 'reports/call-reports',
          onClick: () => onNavigate?.('reports/call-reports'),
        },
        {
          icon: PhoneMissed,
          label: 'Missed Calls',
          isActive: currentPage === 'reports/missed-calls',
          onClick: () => onNavigate?.('reports/missed-calls'),
        },
        {
          icon: Map,
          label: 'Visit Tracker',
          isActive: currentPage === 'reports/visit-tracker',
          onClick: () => onNavigate?.('reports/visit-tracker'),
        },
      ],
    },
    {
      title: 'Device Management',
      icon: Smartphone,
      items: [
        {
          icon: Smartphone,
          label: 'Device Management',
          isActive: currentPage === 'device-management',
          onClick: () => onNavigate?.('device-management'),
        },
      ],
    },
    {
      title: 'Settings',
      icon: Settings,
      items: [
        {
          icon: Settings,
          label: 'App Settings',
          isActive: currentPage === 'settings',
          onClick: () => onNavigate?.('settings'),
        },
      ],
    },
    {
      title: 'Master',
      icon: Database,
      items: [
        {
          icon: Database,
          label: 'Designation',
          isActive: currentPage === 'designation',
          onClick: () => onNavigate?.('designation')
        },
        {
          icon: Database,
          label: 'Country',
          isActive: currentPage === 'country',
          onClick: () => onNavigate?.('country')
        },
        {
          icon: Database,
          label: 'Zone',
          isActive: currentPage === 'zone',
          onClick: () => onNavigate?.('zone')
        },
        {
          icon: Database,
          label: 'Head Quarter',
          isActive: currentPage === 'headquarter',
          onClick: () => onNavigate?.('headquarter')
        },
        {
          icon: Database,
          label: 'Location',
          isActive: currentPage === 'location',
          onClick: () => onNavigate?.('location')
        },
        {
          icon: Database,
          label: 'Doctor Master',
          isActive: currentPage === 'doctor-master',
          onClick: () => onNavigate?.('doctor-master')
        },
        {
          icon: Database,
          label: 'Medicine Master',
          isActive: currentPage === 'medicine-master',
          onClick: () => onNavigate?.('medicine-master')
        },
        {
          icon: Navigation,
          label: 'Route Master',
          isActive: currentPage === 'route-master',
          onClick: () => onNavigate?.('route-master')
        },
        {
          icon: Database,
          label: 'Daily Allowance',
          isActive: currentPage === 'daily-allowance',
          onClick: () => onNavigate?.('daily-allowance')
        },
      ]
    }
  ];

  // FIXED: Auto-expand only the group that contains the active page
  React.useEffect(() => {
    const activeGroup = navigationGroups.find(group =>
      group.items.some(item => item.isActive)
    );

    if (activeGroup) {
      setExpandedGroups(new Set([activeGroup.title])); // only expand this group
    }
  }, [currentPage]);

  // FIXED: Toggle (no duplicate title entries)
  const toggleGroup = (groupTitle: string) => {
    setExpandedGroups(prev => {
      const updated = new Set(prev);
      updated.has(groupTitle) ? updated.delete(groupTitle) : updated.add(groupTitle);
      return updated;
    });
  };

  return (
    <div className={`flex flex-col w-64 shrink-0 bg-white border-r border-gray-200 shadow-lg fixed inset-y-0 left-0 z-30 transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:h-screen lg:z-auto ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Apex Med</h2>
              <p className="text-sm text-blue-600">Admin Portal</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors lg:hidden"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 py-6 overflow-y-auto">
        <div className="px-4">
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
            MAIN NAVIGATION
          </h3>
          <nav className="space-y-1">
            {navigationGroups.map((group, groupIndex) => {
              const GroupIcon = group.icon;
              const isExpanded = expandedGroups.has(group.title);
              const hasMultipleItems = group.items.length > 1;

              return (
                <div key={groupIndex}>
                  {hasMultipleItems ? (
                    <>
                      {/* Group Header (Collapsible) */}
                      <button
                        onClick={() => toggleGroup(group.title)}
                        className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-600 transition-colors rounded-lg hover:bg-gray-50 hover:text-gray-900"
                      >
                        <div className="flex items-center">
                          <GroupIcon className="w-5 h-5 mr-3" />
                          {group.title}
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>

                      {/* Group Items */}
                      {isExpanded && (
                        <div className="mt-1 ml-6 space-y-1">
                          {group.items.map((item, itemIndex) => {
                            const Icon = item.icon;
                            return (
                              <button
                                key={itemIndex}
                                onClick={item.onClick}
                                className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${item.isActive
                                  ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                  }`}
                              >
                                <Icon className="w-4 h-4 mr-3" />
                                {item.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Single Item Groups (Direct Links) */
                    group.items.map((item, itemIndex) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={itemIndex}
                          onClick={item.onClick}
                          className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${item.isActive
                            ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                        >
                          <Icon className="w-5 h-5 mr-3" />
                          {item.label}
                        </button>
                      );
                    })
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center mb-3 space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            {user &&
              <p className="text-sm font-medium text-gray-900">{user.name}</p>
            }
            <p className="text-xs text-gray-500">admin</p>
          </div>
          {/* <div className="flex items-center justify-center w-6 h-6 bg-red-500 rounded-full">
            <span className="text-xs font-bold text-white">12</span>
          </div> */}
        </div>

        <button
          onClick={() => setShowChangePassword(true)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-600 transition-colors rounded-lg hover:bg-gray-50"
        >
          <KeyRound className="w-4 h-4" />
          Change Password
        </button>

        {onLogout && (
          <button
            onClick={onLogout}
            className="w-full px-3 py-2 text-sm text-left text-gray-600 transition-colors rounded-lg hover:bg-gray-50"
          >
            Sign Out
          </button>
        )}
      </div>

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        userId={user?.id ?? 0}
        userName={user?.name ?? "Admin"}
        isSelf
      />
    </div>
  );
};

export default Sidebar;
